<script>
export default {
  name: "AdditionalProgramSessionFees",
};
</script>

<script setup>
import { defineEmits, defineProps, unref, ref } from "vue";
import { useVModel } from "@vueuse/core";
import { useVuelidate } from "@vuelidate/core";
import BasicCheckbox from "@/components/shared/input/BasicCheckbox.vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import { updateFieldToDecimals } from "@/services/utils";
import { convertCost } from "@/components/program-manager/sessions/utils";
import { centsToDollars } from "@/mixins/helpers";
import {
  MEDICAL_INSURANCE,
  SPAIN_MEDICAL_INSURANCE,
} from "@/components/program-manager/sessions/constants";

const props = defineProps({
  modelValue: {
    type: Object,
    required: false,
    default: () => {},
  },
});

const emit = defineEmits(["update:modelValue"]);
const programSession = useVModel(props, "modelValue", emit);
const products = [MEDICAL_INSURANCE, SPAIN_MEDICAL_INSURANCE];

const rules = {
  programSession: {
    group_flight: {},
    public_transportation_card: {},
    medical_insurance: {},
    spain_medical_insurance: {},
  },
};

const v$ = useVuelidate(rules, programSession, {
  $registerAs: "AdditionalProgramSessionFees",
  $lazy: true,
});

const groupFlight = ref(!!unref(programSession)?.group_flight);

const publicTransportationCard = ref(
  !!unref(programSession)?.public_transportation_card
);
const updateObjectModelValue = (val, field) => {
  if (val && !Number.isNaN(val)) {
    unref(programSession)[field] = val;
  } else {
    unref(programSession)[field] = undefined;
  }
};
</script>

<template>
  <ExpandingSection
    field-label="Additional Program Session Fees"
    class="col-span-2"
  >
    <template #content>
      <div class="grid grid-cols-2 gap-x-24 gap-y-4">
        <div>
          <BasicCheckbox
            v-model="groupFlight"
            class="my-auto"
            :vuelidate-instance="v$?.programSession.group_flight"
            field-label="Group Flight"
            @update:model-value="
              (val) => updateObjectModelValue(val, 'group_flight')
            "
          />
          <BasicTextInput
            v-if="groupFlight"
            v-model="programSession.group_flight"
            input-classes="h-10 w-full form-input"
            type="number"
            step="1"
            min="0"
            :vuelidate-instance="v$?.programSession.group_flight"
            outer-classes="col-span-2 flex dollar-input items-center mt-2"
            @blur="() => updateFieldToDecimals('group_flight', programSession)"
          >
            <template #fieldLabel>
              <span class="common-label-text mr-2">$</span>
            </template>
          </BasicTextInput>
        </div>
        <div>
          <BasicCheckbox
            v-model="publicTransportationCard"
            class="my-auto"
            field-label="Public Transportation Card"
            :vuelidate-instance="v$?.programSession.public_transportation_card"
            @update:model-value="
              (val) => updateObjectModelValue(val, 'public_transportation_card')
            "
          />
          <BasicTextInput
            v-if="publicTransportationCard"
            v-model="programSession.public_transportation_card"
            input-classes="h-10 w-full form-input"
            type="number"
            step="1"
            min="0"
            :vuelidate-instance="v$?.programSession.public_transportation_card"
            outer-classes="col-span-2 flex dollar-input items-center mt-2"
            @blur="
              () =>
                updateFieldToDecimals(
                  'public_transportation_card',
                  programSession
                )
            "
          >
            <template #fieldLabel>
              <span class="common-label-text mr-2">$</span>
            </template>
          </BasicTextInput>
        </div>
        <div v-for="product in products" :key="product.id">
          <div class="col-span-1">
            <BasicCheckbox
              v-model="programSession[product.key]"
              field-label-classes="text-capitalize"
              :vuelidate-instance="v$?.programSession[product.key]"
              :field-label="product.name"
              @update:model-value="
                (val) =>
                  updateObjectModelValue(
                    val ? product.price_in_cents : undefined,
                    product.key
                  )
              "
            />
            <div
              :class="[
                { 'text-gray-400': !programSession[product.key] },
                'mt-3',
              ]"
            >
              {{ convertCost(centsToDollars(product.price_in_cents)) }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </ExpandingSection>
</template>
